import { __awaiter, __generator, __read, __spreadArray } from "tslib";
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useEdgeworthConfig } from '@office-start/edgeworth-config-service';
import { useFolders, useInitializeFolders, } from '@office-start/folders-service';
import { useIdentity } from '@office-start/identity-service';
import { logError } from '@office-start/telemetry-service';
import { useUserEntitlements } from '@office-start/user-entitlements-service';
import { useCallback, useEffect, useMemo } from 'react';
import create from 'zustand';
import { documentsCardId } from '../models/Constants';
import { getSortedMapByValue } from '../utilities/getSortedMapByValue';
import { getSortedSet } from '../utilities/getSortedSet';
import { convertDocumentToCard, logTagFeature, logTagMetrics, } from '../utilities/TagsUtils';
import { useShouldInitializeFolders } from './useShouldInitializeFolders';
var tagCompareFn = function (tag1, tag2) {
    return tag1.value.localeCompare(tag2.value);
};
var tagMapValueCompareFn = function (value1, value2) { return tagCompareFn(value1[0], value2[0]); };
var getSortedSetCompareFn = function (tagMap) {
    return function (item1, item2) {
        if (tagMap.has(item1) && tagMap.has(item2)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            var tag1 = tagMap.get(item1)[0];
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            var tag2 = tagMap.get(item2)[0];
            return tagCompareFn(tag1, tag2);
        }
        return 0;
    };
};
var useEdgeworthTagsStore = create(function (set) { return ({
    docIdToTagIdsMap: new Map(),
    setDocIdToTagIdsMap: function (docIdToTagIdsMap) {
        set({ docIdToTagIdsMap: docIdToTagIdsMap });
    },
    tagIdToItemsDocumentIdsMap: new Map(),
    setTagIdToItemsDocumentIdsMap: function (tagIdToItemsDocumentIdsMap) {
        set({ tagIdToItemsDocumentIdsMap: tagIdToItemsDocumentIdsMap });
    },
}); });
export var useSingletonEdgeworthTags = function () {
    var _a;
    var componentContext = useOfficeComponentContext().data;
    var identity = useIdentity().data;
    var edgeworthConfig = useEdgeworthConfig().data;
    var userEntitlements = useUserEntitlements().data;
    var shouldEnableTagging = !!(((_a = userEntitlements === null || userEntitlements === void 0 ? void 0 : userEntitlements.result) === null || _a === void 0 ? void 0 : _a.OneDriveResourceId) &&
        (edgeworthConfig === null || edgeworthConfig === void 0 ? void 0 : edgeworthConfig.taggingEnabled));
    var shouldInitializeFolders = useShouldInitializeFolders();
    useInitializeFolders(shouldEnableTagging && shouldInitializeFolders);
    var folders = useFolders();
    var _b = useEdgeworthTagsStore(), docIdToTagIdsMap = _b.docIdToTagIdsMap, setDocIdToTagIdsMap = _b.setDocIdToTagIdsMap, tagIdToItemsDocumentIdsMap = _b.tagIdToItemsDocumentIdsMap, setTagIdToItemsDocumentIdsMap = _b.setTagIdToItemsDocumentIdsMap;
    var getOrCreateDocumentCard = useCallback(function (document) { return __awaiter(void 0, void 0, void 0, function () {
        var documentCard, edgeworthDocumentCard, _a, documentsCards;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!folders) {
                        return [2 /*return*/, undefined];
                    }
                    documentCard = folders.foldersClient.getCard(document.id);
                    if (!!documentCard) return [3 /*break*/, 7];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    edgeworthDocumentCard = convertDocumentToCard(document);
                    return [4 /*yield*/, folders.foldersClient.createCard(edgeworthDocumentCard)];
                case 2:
                    _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    return [2 /*return*/, undefined];
                case 4:
                    documentsCards = Array.from(folders.foldersClient.viewBy('fullView', {
                        cardId: documentsCardId,
                    }));
                    if (!!documentsCards.some(function (card) { return card.uniqueId === document.id; })) return [3 /*break*/, 6];
                    return [4 /*yield*/, folders.foldersClient.addCard(document.id, documentsCardId)];
                case 5:
                    _b.sent();
                    _b.label = 6;
                case 6:
                    documentCard = folders.foldersClient.getCard(document.id);
                    _b.label = 7;
                case 7: return [2 /*return*/, documentCard];
            }
        });
    }); }, [folders]);
    /**
     * Creates a tag if needed and tag a document
     */
    var createTag = useMemo(function () {
        return function (tag, document) { return __awaiter(void 0, void 0, void 0, function () {
            var documentCard, tagId, existingTag, _error_1, _error_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!folders) {
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, getOrCreateDocumentCard(document)];
                    case 1:
                        documentCard = _a.sent();
                        if (!!documentCard) return [3 /*break*/, 2];
                        logTagFeature('TagCreate', false, 'Document_Card_Undefined');
                        return [3 /*break*/, 12];
                    case 2:
                        tagId = void 0;
                        existingTag = folders.foldersClient.getTagByValue(tag.value);
                        if (!!existingTag) return [3 /*break*/, 7];
                        _a.label = 3;
                    case 3:
                        _a.trys.push([3, 5, , 6]);
                        return [4 /*yield*/, folders.foldersClient.createTag(tag)];
                    case 4:
                        tagId = _a.sent();
                        logTagFeature('TagCreate', true);
                        return [3 /*break*/, 6];
                    case 5:
                        _error_1 = _a.sent();
                        //TODO Sync with MFS team for better error logs
                        // TODO pass error on throw new error
                        logTagFeature('TagCreate', false, 'Tag_Create_Error');
                        throw new Error('Tag create error');
                    case 6: return [3 /*break*/, 8];
                    case 7:
                        tagId = existingTag.uniqueId;
                        _a.label = 8;
                    case 8:
                        if (!tagId) return [3 /*break*/, 12];
                        _a.label = 9;
                    case 9:
                        _a.trys.push([9, 11, , 12]);
                        return [4 /*yield*/, folders.foldersClient.tag(document.id, tagId)];
                    case 10:
                        _a.sent();
                        logTagFeature('TagCard', true);
                        return [3 /*break*/, 12];
                    case 11:
                        _error_2 = _a.sent();
                        logTagFeature('TagCard', false, 'Data_Error');
                        throw new Error('Tagging card error'); // TODO pass error here
                    case 12: return [2 /*return*/];
                }
            });
        }); };
    }, [getOrCreateDocumentCard, folders]);
    /**
     * Updates / patches a tag
     */
    var patchTag = useMemo(function () {
        return function (originalTag, newTag) { return __awaiter(void 0, void 0, void 0, function () {
            var existingTag;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!folders) {
                            return [2 /*return*/];
                        }
                        existingTag = folders.foldersClient.getTag(originalTag.uniqueId);
                        if (!!existingTag) return [3 /*break*/, 1];
                        logTagFeature('TagEdit', false, 'Original_Tag_Undefined');
                        return [3 /*break*/, 3];
                    case 1: return [4 /*yield*/, folders.foldersClient
                            .patchTag(originalTag.uniqueId, newTag)
                            .then(function () {
                            logTagFeature('TagEdit', true);
                        })
                            .catch(function () {
                            //TODO Sync with MFS team for better error logs
                            //TODO throw error if required to consume elsewhere/for UI updates (like toast)
                            logTagFeature('TagEdit', false, 'Tag_Edit_Error');
                        })];
                    case 2:
                        _a.sent();
                        _a.label = 3;
                    case 3: return [2 /*return*/];
                }
            });
        }); };
    }, [folders]);
    /**
     * Untag/remove tag for a document.
     */
    var removeTag = useMemo(function () {
        return function (tag, document) { return __awaiter(void 0, void 0, void 0, function () {
            var _error_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!folders) {
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, folders.foldersClient.untag(document.id, tag.uniqueId)];
                    case 2:
                        _a.sent();
                        logTagFeature('TagRemove', true);
                        return [3 /*break*/, 4];
                    case 3:
                        _error_3 = _a.sent();
                        //TODO Sync with MFS team for better error logs
                        logTagFeature('TagRemove', false, 'Tag_Remove_Error');
                        throw new Error('Tag removed error'); //TODO pass in the error here if required
                    case 4: return [2 /*return*/];
                }
            });
        }); };
    }, [folders]);
    /**
     * Delete a tag from the list of tags. Remove tag from all documents.
     */
    var deleteTag = useMemo(function () {
        return function (tag) { return __awaiter(void 0, void 0, void 0, function () {
            var _error_4;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!folders) {
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, folders.foldersClient.deleteTag(tag.uniqueId)];
                    case 2:
                        _a.sent();
                        logTagFeature('TagDelete', true);
                        return [3 /*break*/, 4];
                    case 3:
                        _error_4 = _a.sent();
                        //TODO Sync with MFS team for better error logs
                        //TODO throw error if required to consume elsewhere
                        logTagFeature('TagDelete', false, 'Tag_Delete_Error');
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
    }, [folders]);
    var getTagIdByTagValue = useMemo(function () {
        return function (tagValue) {
            if (!folders) {
                return undefined;
            }
            var tag = folders.foldersClient.getTagByValue(tagValue);
            if (!tag) {
                return undefined;
            }
            return tag.uniqueId;
        };
    }, [folders]);
    useEffect(function () {
        if (!componentContext || !identity || !shouldEnableTagging) {
            return;
        }
        var isMounted = true;
        var createFoldersClient = function () { return __awaiter(void 0, void 0, void 0, function () {
            var documentsFolderCard_1, createCard, errorType, retryErrorType, e_1, tags, documentCards, initialTagIdToItemsDocumentIdsMap, initialDocumentIdToTagIdsMap;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!folders) {
                            return [2 /*return*/];
                        }
                        logTagFeature('InitializeFoldersClient', true);
                        if (!!folders.foldersClient.getCard(documentsCardId)) return [3 /*break*/, 7];
                        documentsFolderCard_1 = {
                            uniqueId: documentsCardId,
                            name: 'Edgeworth Documents',
                            type: folders.foldersModule.Folder,
                        };
                        createCard = function () { return __awaiter(void 0, void 0, void 0, function () {
                            var errorType, error_1;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        if (!folders) {
                                            return [2 /*return*/, undefined];
                                        }
                                        _a.label = 1;
                                    case 1:
                                        _a.trys.push([1, 3, , 4]);
                                        return [4 /*yield*/, folders.foldersClient.createCard(documentsFolderCard_1, false)];
                                    case 2:
                                        _a.sent();
                                        return [3 /*break*/, 4];
                                    case 3:
                                        error_1 = _a.sent();
                                        errorType = 'Create_Card_Error';
                                        if (error_1 instanceof
                                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                            folders.foldersModule.ResourceIdAlreadyInUse) {
                                            errorType = 'Create_Card_ResourceIdInUse_Error';
                                        }
                                        else if (error_1 instanceof
                                            folders.foldersModule.InvalidOperation) {
                                            errorType = 'Create_Card_InvalidOperation_Error';
                                        }
                                        return [3 /*break*/, 4];
                                    case 4: return [2 /*return*/, errorType];
                                }
                            });
                        }); };
                        return [4 /*yield*/, createCard()];
                    case 1:
                        errorType = _a.sent();
                        if (!!errorType) return [3 /*break*/, 2];
                        logTagFeature('InitializeFolder', true);
                        return [3 /*break*/, 7];
                    case 2:
                        //TODO throw with error here if required
                        logTagFeature('InitializeFolder', false, errorType);
                        if (!(errorType === 'Create_Card_ResourceIdInUse_Error')) return [3 /*break*/, 7];
                        _a.label = 3;
                    case 3:
                        _a.trys.push([3, 6, , 7]);
                        return [4 /*yield*/, folders.foldersClient.deleteCard(documentsCardId)];
                    case 4:
                        _a.sent();
                        logTagFeature('InitializeFolder_DeleteCard', true);
                        return [4 /*yield*/, createCard()];
                    case 5:
                        retryErrorType = _a.sent();
                        logTagFeature('InitializeFolder_Retry', !retryErrorType, retryErrorType);
                        return [3 /*break*/, 7];
                    case 6:
                        e_1 = _a.sent();
                        logTagFeature('InitializeFolder_DeleteCard', false);
                        return [3 /*break*/, 7];
                    case 7:
                        tags = Array.from(folders.foldersClient.getTags());
                        // Sort tags by tag name
                        tags.sort(tagCompareFn);
                        documentCards = Array.from(folders.foldersClient.viewBy('fullView', {
                            cardId: documentsCardId,
                        }));
                        initialTagIdToItemsDocumentIdsMap = new Map(tags.map(function (tag) { return [tag.uniqueId, [tag, new Set()]]; }));
                        initialDocumentIdToTagIdsMap = new Map();
                        documentCards.forEach(function (card) {
                            // Skip parent card since it is returned from viewBy() above
                            if (card.uniqueId === documentsCardId) {
                                return;
                            }
                            var tagsArray = __spreadArray([], __read(card.tags), false);
                            // Sort tags by tag name
                            tagsArray.sort(tagCompareFn);
                            initialDocumentIdToTagIdsMap.set(card.uniqueId, new Set(tagsArray.map(function (tag) { return tag.uniqueId; })));
                            tagsArray.forEach(function (tag) {
                                if (!initialTagIdToItemsDocumentIdsMap.has(tag.uniqueId)) {
                                    logError({
                                        eventName: 'Tag_Error',
                                        errorType: 'Tag_Mismatch_Error',
                                    });
                                }
                                else {
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    initialTagIdToItemsDocumentIdsMap
                                        .get(tag.uniqueId)[1]
                                        .add(card.uniqueId);
                                }
                            });
                        });
                        if (isMounted) {
                            setDocIdToTagIdsMap(initialDocumentIdToTagIdsMap);
                            setTagIdToItemsDocumentIdsMap(initialTagIdToItemsDocumentIdsMap);
                        }
                        logTagMetrics(initialTagIdToItemsDocumentIdsMap);
                        return [2 /*return*/];
                }
            });
        }); };
        createFoldersClient();
        return function () {
            isMounted = false;
        };
    }, [
        componentContext,
        edgeworthConfig,
        identity,
        folders,
        setDocIdToTagIdsMap,
        setTagIdToItemsDocumentIdsMap,
    ]);
    useEffect(function () {
        if (folders) {
            var tagUnsubscribe_1 = folders.foldersClient.onScopedEvent(documentsCardId, 'tagged', function (taggedEvent) {
                var tagItem = taggedEvent.tag;
                var tagId = tagItem.uniqueId;
                var docId = taggedEvent.taggedCard.uniqueId;
                var newTagMap = new Map(tagIdToItemsDocumentIdsMap);
                var newDocMap = new Map(docIdToTagIdsMap);
                if (!newTagMap.has(tagId)) {
                    newTagMap.set(tagId, [
                        tagItem,
                        new Set([docId]),
                    ]);
                    // Sort tags in map by tag value
                    newTagMap = getSortedMapByValue(newTagMap, tagMapValueCompareFn);
                }
                else {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    newTagMap.get(tagId)[1].add(docId);
                }
                if (!newDocMap.has(docId)) {
                    newDocMap.set(docId, new Set([tagId]));
                }
                else {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    var tagSet = newDocMap.get(docId);
                    tagSet.add(tagId);
                    var newTagIdSet = getSortedSet(tagSet, getSortedSetCompareFn(newTagMap));
                    newDocMap.set(docId, newTagIdSet);
                }
                setTagIdToItemsDocumentIdsMap(newTagMap);
                setDocIdToTagIdsMap(newDocMap);
            });
            var untagUnsubscribe_1 = folders.foldersClient.onScopedEvent(documentsCardId, 'untagged', function (taggedEvent) {
                var tagItem = taggedEvent.tag;
                var tagId = tagItem.uniqueId;
                var docId = taggedEvent.untaggedCard.uniqueId;
                if (tagIdToItemsDocumentIdsMap.has(tagId)) {
                    var newTagMap = new Map(tagIdToItemsDocumentIdsMap);
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    var docIdSet = newTagMap.get(tagId)[1];
                    if (docIdSet.has(docId)) {
                        docIdSet.delete(docId);
                        setTagIdToItemsDocumentIdsMap(newTagMap);
                    }
                }
                if (docIdToTagIdsMap.has(docId)) {
                    var newDocMap = new Map(docIdToTagIdsMap);
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    var tagIdSet = newDocMap.get(docId);
                    //if the doc has this tag, remove it
                    if (tagIdSet.has(tagId)) {
                        if (tagIdSet.size === 1) {
                            newDocMap.delete(docId);
                        }
                        else {
                            tagIdSet.delete(tagId);
                        }
                        setDocIdToTagIdsMap(newDocMap);
                    }
                }
            });
            var tagDeletedUnsubscribe_1 = folders.foldersClient.onUnscopedEvent('tagDeleted', function (tagDeletedEvent) {
                var tagId = tagDeletedEvent.tag.uniqueId;
                var newTagMap = new Map(tagIdToItemsDocumentIdsMap);
                var newDocMap = new Map(docIdToTagIdsMap);
                if (newTagMap.has(tagId)) {
                    var docIdSet = 
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    newTagMap.get(tagId)[1];
                    if (docIdSet.size > 0) {
                        Array.from(docIdSet.keys()).forEach(function (docId) {
                            if (newDocMap.has(docId)) {
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                var tagIdSet = newDocMap.get(docId);
                                //if the doc has this tag, remove it
                                if (tagIdSet.has(tagId)) {
                                    if (tagIdSet.size === 1) {
                                        newDocMap.delete(docId);
                                    }
                                    else {
                                        tagIdSet.delete(tagId);
                                    }
                                }
                            }
                        });
                        setDocIdToTagIdsMap(newDocMap);
                    }
                    newTagMap.delete(tagId);
                    setTagIdToItemsDocumentIdsMap(newTagMap);
                }
            });
            var tagPatchedUnsubscribe_1 = folders.foldersClient.onUnscopedEvent('tagPatched', function (tagPatchedEvent) {
                var newHarmonyTag = tagPatchedEvent.tag;
                var tagId = newHarmonyTag.uniqueId;
                if (tagId && tagIdToItemsDocumentIdsMap.has(tagId)) {
                    var newTagMap_1 = new Map(tagIdToItemsDocumentIdsMap);
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    var _a = __read(newTagMap_1.get(tagId), 2), oldTag = _a[0], docIdSet = _a[1];
                    newTagMap_1.set(tagId, [newHarmonyTag, docIdSet]);
                    var hasNewTagValue = oldTag.value !== newHarmonyTag.value;
                    setTagIdToItemsDocumentIdsMap(hasNewTagValue
                        ? getSortedMapByValue(newTagMap_1, tagMapValueCompareFn)
                        : newTagMap_1);
                    if (hasNewTagValue) {
                        var newDocMap_1 = new Map(docIdToTagIdsMap);
                        Array.from(docIdSet.keys()).forEach(function (docId) {
                            if (newDocMap_1.has(docId)) {
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                var tagSet = newDocMap_1.get(docId);
                                var newTagIdSet = getSortedSet(tagSet, getSortedSetCompareFn(newTagMap_1));
                                newDocMap_1.set(docId, newTagIdSet);
                            }
                        });
                        setDocIdToTagIdsMap(newDocMap_1);
                    }
                }
            });
            return function () {
                tagUnsubscribe_1();
                untagUnsubscribe_1();
                tagDeletedUnsubscribe_1();
                tagPatchedUnsubscribe_1();
            };
        }
        return undefined;
    }, [
        docIdToTagIdsMap,
        folders,
        setDocIdToTagIdsMap,
        setTagIdToItemsDocumentIdsMap,
        tagIdToItemsDocumentIdsMap,
    ]);
    return useMemo(function () {
        if (!shouldEnableTagging) {
            return undefined;
        }
        return {
            tagIdToItemsDocumentIdsMap: tagIdToItemsDocumentIdsMap,
            docIdToTagIdsMap: docIdToTagIdsMap,
            createTag: createTag,
            patchTag: patchTag,
            deleteTag: deleteTag,
            removeTag: removeTag,
            getTagIdByTagValue: getTagIdByTagValue,
        };
    }, [
        createTag,
        deleteTag,
        docIdToTagIdsMap,
        getTagIdByTagValue,
        patchTag,
        removeTag,
        shouldEnableTagging,
        tagIdToItemsDocumentIdsMap,
    ]);
};
//# sourceMappingURL=useSingletonEdgeworthTags.js.map